import React, { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { playSoundEffect } from '../hooks/useSoundEffects';
import { getMaxStarsForRankIndex, getRankKey } from './devPvpLadderMock';
import { RankInsigniaVertical, RANK_INSIGNIA } from './RankInsigniaVertical';
import './PvpRankProgressScreen.css';

function rankNameTKey(rankKey) {
  if (!rankKey || typeof rankKey !== 'string') {
    throw new Error('PvpRankProgressScreen: rankKey is required');
  }
  return `pvpRank.ranks.${rankKey}`;
}

/** SVG viewBox units — must match star orbit radius in CSS/JS (stars sit on stroke centerline). */
const RING_RADIUS_VIEWBOX = 38;
const RING_STROKE_VIEWBOX = 4;
const RING_CIRCUMFERENCE_VIEWBOX = 2 * Math.PI * RING_RADIUS_VIEWBOX;

/**
 * Promotion sequence (ms):
 * 1) FILL_STAR — last orbit star turns gold (star_added).
 * 2) PAUSE_AFTER_LAST_STAR — hold full old rank; no rank change (default 2s).
 * 3) FLASH_HOLD — glow overlay, then reveal new rank.
 */
const PROMO_MS_FILL_STAR = 120;
const PROMO_MS_PAUSE_AFTER_LAST_STAR = 2000;
const PROMO_MS_FLASH_HOLD = 650;

const PROMO_MS_START_GLOW = PROMO_MS_FILL_STAR + PROMO_MS_PAUSE_AFTER_LAST_STAR;
const PROMO_MS_SHOW_NEXT_RANK = PROMO_MS_START_GLOW + PROMO_MS_FLASH_HOLD;

/** Same-rank star gain/loss: show pre-battle state, then animate ring + orbit to snapshot (with SFX). */
const MS_STAR_STEP_HOLD_BEFORE = 550;

function PvpRankProgressSoundEffects({ shouldPromoAnimate, promoPhase }) {
  const prevPromoPhaseForSoundRef = useRef(null);

  useEffect(() => {
    const prev = prevPromoPhaseForSoundRef.current;
    prevPromoPhaseForSoundRef.current = promoPhase;
    if (prev === null) return;
    if (!shouldPromoAnimate) return;
    // New rank + bright flash mount together; levelup must align with first frame of that reveal.
    if (prev === 'fill' && promoPhase === 'flash') {
      playSoundEffect('/ranking/levelup.mp3');
    }
  }, [shouldPromoAnimate, promoPhase]);

  return null;
}

/**
 * @param {object} props
 * @param {object} props.pvpLadder - Snapshot from server
 * @param {'win'|'loss'} props.result
 * @param {() => void} props.onProceed
 * @param {boolean} [props.devSimulateWinEnabled] - Dev mock: show +1 win control (BattleOverview devPvpRankMock only)
 * @param {() => void} [props.onDevSimulateWin]
 */
function PvpRankProgressScreen({
  pvpLadder,
  result,
  onProceed,
  devSimulateWinEnabled = false,
  onDevSimulateWin
}) {
  const { t } = useTranslation();
  const ringGoldGradientId = useId().replace(/:/g, '');
  const ringRef = useRef(null);
  const [orbitRadiusPx, setOrbitRadiusPx] = useState(142);

  const isValidPvpLadder = pvpLadder != null && typeof pvpLadder === 'object';
  const rankKey = isValidPvpLadder ? pvpLadder.rankKey : '';
  const stars = isValidPvpLadder ? Number(pvpLadder.stars) : NaN;
  const maxStars = isValidPvpLadder ? Number(pvpLadder.maxStars) : NaN;
  const inLeague = isValidPvpLadder && pvpLadder.inLeague === true;
  const promoted = isValidPvpLadder && pvpLadder.promoted === true;
  const reachedGeneral = isValidPvpLadder && pvpLadder.reachedGeneral === true;
  const rankIndex =
    isValidPvpLadder && pvpLadder.rankIndex != null && Number.isFinite(Number(pvpLadder.rankIndex))
      ? Number(pvpLadder.rankIndex)
      : null;

  const oldRankIndexForPromotion = useMemo(() => {
    if (!promoted || result !== 'win') return null;
    if (reachedGeneral) return 8;
    if (rankIndex != null && rankIndex >= 1 && rankIndex <= 8) return rankIndex - 1;
    return null;
  }, [promoted, result, reachedGeneral, rankIndex]);

  const shouldPromoAnimate =
    oldRankIndexForPromotion != null && promoted && result === 'win';

  const promotionAnimKey = useMemo(() => {
    if (!shouldPromoAnimate || oldRankIndexForPromotion == null || rankIndex == null) {
      return null;
    }
    return `${oldRankIndexForPromotion}-${rankIndex}`;
  }, [shouldPromoAnimate, oldRankIndexForPromotion, rankIndex]);

  const [promoPhase, setPromoPhase] = useState(() => (shouldPromoAnimate ? 'fill' : 'done'));
  const [fillComplete, setFillComplete] = useState(false);
  const [starStepPhase, setStarStepPhase] = useState('after');

  const inPromoSequence = shouldPromoAnimate && promoPhase !== 'done';

  /** Old rank ring + insignia only during fill/pause; flash + done use new rank (hidden under glow until fade-in). */
  const promoShowsOldRankVisual =
    shouldPromoAnimate && oldRankIndexForPromotion != null && promoPhase === 'fill';

  useLayoutEffect(() => {
    if (!shouldPromoAnimate || promotionAnimKey == null) {
      return undefined;
    }
    setPromoPhase('fill');
    setFillComplete(false);
    let cancelled = false;
    const tFill = window.setTimeout(() => {
      if (!cancelled) {
        playSoundEffect('/ranking/star_added.mp3');
        setFillComplete(true);
      }
    }, PROMO_MS_FILL_STAR);
    const tFlash = window.setTimeout(() => {
      if (!cancelled) setPromoPhase('flash');
    }, PROMO_MS_START_GLOW);
    const tDone = window.setTimeout(() => {
      if (!cancelled) setPromoPhase('done');
    }, PROMO_MS_SHOW_NEXT_RANK);
    return () => {
      cancelled = true;
      window.clearTimeout(tFill);
      window.clearTimeout(tFlash);
      window.clearTimeout(tDone);
    };
  }, [shouldPromoAnimate, promotionAnimKey]);

  const showInsignia = isValidPvpLadder && RANK_INSIGNIA[rankKey] != null;
  const showWinsProgress =
    isValidPvpLadder &&
    !inLeague &&
    rankKey !== 'general' &&
    maxStars > 0 &&
    Number.isFinite(stars) &&
    Number.isFinite(maxStars);

  const showWinsProgressRing = inPromoSequence || showWinsProgress;

  const winsEarnedDisplay = Math.max(0, Math.min(Math.floor(stars), maxStars));

  const starsDeltaNum =
    isValidPvpLadder &&
    pvpLadder.starsDelta != null &&
    Number.isFinite(Number(pvpLadder.starsDelta))
      ? Number(pvpLadder.starsDelta)
      : null;

  const needsStarStepAnimation = useMemo(() => {
    if (!showWinsProgress) return false;
    if (shouldPromoAnimate) return false;
    if (inLeague) return false;
    if (rankKey === 'general') return false;
    if (result === 'win' && starsDeltaNum === 1 && !promoted) return true;
    if (result === 'loss' && starsDeltaNum === -1) return true;
    return false;
  }, [
    showWinsProgress,
    shouldPromoAnimate,
    inLeague,
    rankKey,
    result,
    starsDeltaNum,
    promoted,
  ]);

  const starsBeforeWinLoss = useMemo(() => {
    if (!needsStarStepAnimation) return null;
    if (result === 'win') {
      const b = winsEarnedDisplay - 1;
      if (b < 0) {
        throw new Error(`PvpRankProgressScreen: win star step invalid starsBefore=${b}`);
      }
      return b;
    }
    const b = winsEarnedDisplay + 1;
    if (b > maxStars) {
      throw new Error(
        `PvpRankProgressScreen: loss star step invalid starsBefore=${b} maxStars=${maxStars}`
      );
    }
    return b;
  }, [needsStarStepAnimation, result, winsEarnedDisplay, maxStars]);

  useEffect(() => {
    if (!needsStarStepAnimation) {
      setStarStepPhase('after');
      return;
    }
    setStarStepPhase('before');
    const tid = window.setTimeout(() => {
      if (result === 'win') {
        playSoundEffect('/ranking/star_added.mp3');
      } else {
        playSoundEffect('/ranking/star_removed.mp3');
      }
      setStarStepPhase('after');
    }, MS_STAR_STEP_HOLD_BEFORE);
    return () => window.clearTimeout(tid);
  }, [needsStarStepAnimation, result, rankIndex, winsEarnedDisplay, starsDeltaNum]);

  useLayoutEffect(() => {
    if (!showWinsProgressRing) return;
    const el = ringRef.current;
    if (!el) return;
    const update = () => {
      const w = el.offsetWidth;
      if (!w) return;
      setOrbitRadiusPx(w * (RING_RADIUS_VIEWBOX / 100));
    };
    update();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    if (ro) ro.observe(el);
    window.addEventListener('resize', update);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [showWinsProgressRing, maxStars, inPromoSequence, oldRankIndexForPromotion]);

  if (!isValidPvpLadder) {
    throw new Error('PvpRankProgressScreen: pvpLadder is required');
  }
  if (devSimulateWinEnabled && typeof onDevSimulateWin !== 'function') {
    throw new Error('PvpRankProgressScreen: onDevSimulateWin is required when devSimulateWinEnabled');
  }

  const displayRankKeyForUi = promoShowsOldRankVisual
    ? getRankKey(oldRankIndexForPromotion)
    : rankKey;
  const rankLabel = t(rankNameTKey(displayRankKeyForUi));

  const displayMaxStarsForRing = promoShowsOldRankVisual
    ? getMaxStarsForRankIndex(oldRankIndexForPromotion)
    : maxStars;

  const nonPromoOrbitStars =
    needsStarStepAnimation && starStepPhase === 'before' && starsBeforeWinLoss != null
      ? starsBeforeWinLoss
      : winsEarnedDisplay;

  const orbitGoldStarCount =
    shouldPromoAnimate && oldRankIndexForPromotion != null
      ? promoShowsOldRankVisual
        ? fillComplete
          ? getMaxStarsForRankIndex(oldRankIndexForPromotion)
          : Math.max(0, getMaxStarsForRankIndex(oldRankIndexForPromotion) - 1)
        : winsEarnedDisplay
      : nonPromoOrbitStars;

  const winsProgressFractionForRing =
    shouldPromoAnimate && oldRankIndexForPromotion != null
      ? promoShowsOldRankVisual
        ? fillComplete
          ? 1
          : (() => {
              const oldMax = getMaxStarsForRankIndex(oldRankIndexForPromotion);
              return oldMax > 0 ? (oldMax - 1) / oldMax : 0;
            })()
        : maxStars > 0
          ? Math.min(1, Math.max(0, winsEarnedDisplay / maxStars))
          : 0
      : maxStars > 0
        ? Math.min(1, Math.max(0, nonPromoOrbitStars / maxStars))
        : 0;

  const displayRankKeyForInsignia = promoShowsOldRankVisual
    ? getRankKey(oldRankIndexForPromotion)
    : rankKey;

  const rankHeadingReactKey = shouldPromoAnimate
    ? promoShowsOldRankVisual
      ? `promo-fill-${oldRankIndexForPromotion}`
      : `promo-reveal-${rankKey}`
    : rankKey;

  const insigniaHoleReactKey = shouldPromoAnimate
    ? promoShowsOldRankVisual
      ? `hole-fill-${oldRankIndexForPromotion}`
      : `hole-reveal-${rankKey}`
    : `hole-${rankKey}`;

  let starsNote = '';
  if (inLeague && maxStars === 0) {
    starsNote = t('pvpRank.inLeagueMmr');
  } else if (Number(pvpLadder.starsDelta) === -1) {
    starsNote = t('pvpRank.winsLost');
  }

  const ringProgressDashOffset =
    RING_CIRCUMFERENCE_VIEWBOX * (1 - winsProgressFractionForRing);

  const orbitStarSlotCount =
    Number.isFinite(displayMaxStarsForRing) && displayMaxStarsForRing > 0
      ? Math.floor(displayMaxStarsForRing)
      : 0;

  return (
    <div className="pvp-rank-progress-overlay" role="dialog" aria-labelledby="pvp-rank-heading">
      <div className="pvp-rank-progress-panel">
        <PvpRankProgressSoundEffects shouldPromoAnimate={shouldPromoAnimate} promoPhase={promoPhase} />
        <p className="pvp-rank-progress-title" id="pvp-rank-heading">
          {t('pvpRank.screenTitle')}
        </p>
        <h2
          key={rankHeadingReactKey}
          className={`pvp-rank-progress-rank-name${
            shouldPromoAnimate && !promoShowsOldRankVisual ? ' pvp-rank-progress-rank-name--promo-fade-in' : ''
          }`}
        >
          {rankLabel}
        </h2>

        {showWinsProgressRing ? (
          <div
            className={`pvp-rank-progress-ring-assembly${promoPhase === 'flash' ? ' pvp-rank-progress-ring-assembly--promo-flash' : ''}`}
            ref={ringRef}
          >
            <div className="pvp-rank-progress-gold-ring-border">
              <svg
                className={`pvp-rank-progress-ring-svg${
                  shouldPromoAnimate && !promoShowsOldRankVisual
                    ? ' pvp-rank-progress-ring-svg--promo-fade-in'
                    : ''
                }`}
                viewBox="0 0 100 100"
                aria-hidden
                focusable="false"
              >
                <defs>
                  <linearGradient
                    id={ringGoldGradientId}
                    x1="0%"
                    y1="0%"
                    x2="100%"
                    y2="100%"
                  >
                    <stop offset="0%" stopColor="#fffef0" />
                    <stop offset="35%" stopColor="#f0d060" />
                    <stop offset="70%" stopColor="#c9a227" />
                    <stop offset="100%" stopColor="#8a6a15" />
                  </linearGradient>
                </defs>
                <g transform="rotate(-90 50 50)">
                  <circle
                    className="pvp-rank-progress-ring-track"
                    cx="50"
                    cy="50"
                    r={RING_RADIUS_VIEWBOX}
                    fill="none"
                    strokeWidth={RING_STROKE_VIEWBOX}
                  />
                  <circle
                    className="pvp-rank-progress-ring-progress"
                    cx="50"
                    cy="50"
                    r={RING_RADIUS_VIEWBOX}
                    fill="none"
                    stroke={`url(#${ringGoldGradientId})`}
                    strokeWidth={RING_STROKE_VIEWBOX}
                    strokeLinecap="round"
                    strokeDasharray={RING_CIRCUMFERENCE_VIEWBOX}
                    strokeDashoffset={ringProgressDashOffset}
                  />
                </g>
              </svg>
              <div
                key={insigniaHoleReactKey}
                className={`pvp-rank-progress-gold-ring-hole${
                  shouldPromoAnimate && !promoShowsOldRankVisual
                    ? ' pvp-rank-progress-gold-ring-hole--promo-fade-in'
                    : ''
                }`}
              >
                {(shouldPromoAnimate
                  ? RANK_INSIGNIA[displayRankKeyForInsignia] != null
                  : showInsignia) && (
                  <RankInsigniaVertical rankKey={displayRankKeyForInsignia} />
                )}
              </div>
              {promoPhase === 'flash' && (
                <div className="pvp-rank-promo-flash-overlay" aria-hidden />
              )}
              {Array.from({ length: orbitStarSlotCount }, (_, i) => {
                const angleDeg = -90 + ((i + 1) / orbitStarSlotCount) * 360;
                const rad = (angleDeg * Math.PI) / 180;
                const x = orbitRadiusPx * Math.cos(rad);
                const y = orbitRadiusPx * Math.sin(rad);
                const src = i < orbitGoldStarCount ? '/ranking/goldstar.png' : '/ranking/blackstar.png';
                const orbitFadeInClass =
                  shouldPromoAnimate && !promoShowsOldRankVisual
                    ? ' pvp-rank-progress-orbit-star--promo-fade-in'
                    : '';
                return (
                  <img
                    key={`${insigniaHoleReactKey}-orbit-${i}`}
                    src={src}
                    alt=""
                    className={`pvp-rank-progress-orbit-star${orbitFadeInClass}`}
                    style={{
                      left: `calc(50% + ${x}px)`,
                      top: `calc(50% + ${y}px)`,
                      transform: 'translate(-50%, -50%)',
                      ...(orbitFadeInClass
                        ? { animationDelay: `${Math.min(i, 12) * 0.045}s` }
                        : undefined),
                    }}
                  />
                );
              })}
            </div>
          </div>
        ) : (
          showInsignia &&
          (rankKey === 'general' ? (
            <div className="pvp-rank-progress-general-insignia">
              <RankInsigniaVertical rankKey={rankKey} />
            </div>
          ) : (
            <RankInsigniaVertical rankKey={rankKey} />
          ))
        )}

        {promoPhase === 'done' && reachedGeneral && (
          <p className="pvp-rank-progress-info">{t('pvpRank.reachedGeneral')}</p>
        )}

        {promoPhase === 'done' && !inLeague && !reachedGeneral && (
          <p className="pvp-rank-progress-info">{t('pvpRank.battleLegendsHint')}</p>
        )}

        {starsNote && !reachedGeneral && promoPhase === 'done' && (
          <p className="pvp-rank-progress-note">{starsNote}</p>
        )}

        {inLeague &&
          promoPhase === 'done' &&
          pvpLadder.mmrDelta != null &&
          pvpLadder.mmrDelta !== 0 && (
          <p className="pvp-rank-progress-mmr">
            {t('pvpRank.mmrChange', {
              delta: pvpLadder.mmrDelta > 0 ? `+${pvpLadder.mmrDelta}` : String(pvpLadder.mmrDelta),
              before: pvpLadder.mmrBefore,
              after: pvpLadder.mmrAfter
            })}
          </p>
        )}

        <div className="pvp-rank-progress-actions">
          {devSimulateWinEnabled && promoPhase === 'done' && (
            <button type="button" className="pvp-rank-progress-dev-win" onClick={onDevSimulateWin}>
              {t('pvpRank.devSimulateWin')}
            </button>
          )}
          <button type="button" className="pvp-rank-progress-proceed" onClick={onProceed}>
            {t('pvpRank.proceed')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PvpRankProgressScreen;
